import _ from 'lodash';

import { isSecondaryDimensionAInEffect } from 'components/V5GadgetForm/isSecondaryDimensionAEligible';

import { GROUP_LIMIT } from '../chartDefinitionParts';
import { DEFAULT_REPORT_CELL_TYPE } from '../buildTableDefinition';
import {
  isChangeColumnSort,
  isInertMatrixSort,
  isUnlimitedMatrix,
  withRenderedMatrixSort,
} from './legacyDefaults';

// As buildTableDefinition defaults them
const DEFAULT_ROW_INTERVAL: FleetOps.Interval = 'month';
const DEFAULT_COLUMN_INTERVAL: FleetOps.Interval = 'week';

const compact = <T extends object>(value: T): T | undefined => {
  const compacted = _.omitBy(value, _.isUndefined);
  return _.isEmpty(compacted) ? undefined : (compacted as T);
};

const isStudioCompareType = (
  compareType: CompareType | undefined,
): compareType is 'previous' | 'lastYear' =>
  compareType === 'previous' || compareType === 'lastYear';

// What its columns are: a date dimension B is the dates, another dimension
// B its terms, else a column per metric
const guessColumns = (
  chartDef: V5ChartDefinition,
): Studio.TableColumns | undefined => {
  if (!chartDef.dimensionB) {
    return undefined;
  }
  return chartDef.dimensionB.fieldType === 'date' ? 'dates' : 'dimension';
};

// Its sort, as buildTableDefinition writes one: by a metric's change, a
// metric, or its rows' own order. Grouped rows by the first metric,
// descending, and date rows in their own order are the defaults.
const guessSort = ({
  chartDef,
  hasDimension,
}: {
  chartDef: V5ChartDefinition;
  hasDimension: boolean;
}): Studio.TableSort | undefined => {
  const sort = chartDef.grouping?.defaultSort;
  const [primary] = chartDef.series;
  // One that sorts nothing (e.g. by a metric since removed) is no sort
  if (!sort || !sort.direction || isInertMatrixSort(chartDef)) {
    return undefined;
  }
  const { direction } = sort;
  // One flagged as a comparison but on a value column sorts by the value
  if (sort.isComparison && sort.metricId && isChangeColumnSort(sort)) {
    return compact<Studio.TableSort>({
      by: 'metric',
      metricId: sort.metricId,
      comparison: (sort.field ?? '').endsWith('-percentDelta')
        ? 'percentDelta'
        : 'delta',
      isAbsolute: sort.isAbsolute || undefined,
      direction,
    });
  }
  const metricId =
    sort.metricId ??
    chartDef.series.find((s) => s.metricId === sort.field)?.metricId;
  if (metricId) {
    const isDefault =
      hasDimension &&
      !!primary &&
      metricId === primary.metricId &&
      direction === 'desc';
    return isDefault ? undefined : { by: 'metric', metricId, direction };
  }
  return { by: 'dimension', direction };
};

// Which rows an exclude top N leaves out, where that isn't the default (the
// sorted, else first, metric, highest first, as buildTableDefinition takes
// it): e.g. the top 10 by MPG while the rest are sorted by miles
const guessExcludeBy = ({
  chartDef,
  excludeSort,
}: {
  chartDef: V5ChartDefinition;
  excludeSort: SortConfig | undefined;
}): Studio.TableExcludeBy | undefined => {
  if (!excludeSort) {
    return undefined;
  }
  const metricId =
    excludeSort.metricId ??
    chartDef.series.find((s) => s.metricId === excludeSort.field)?.metricId;
  if (!metricId) {
    return undefined;
  }
  // Without a direction it leaves out the highest
  // (getExcludeTopNTermResponses)
  const direction = excludeSort.direction === 'asc' ? 'asc' : 'desc';
  const defaultMetricId =
    chartDef.grouping?.defaultSort?.metricId ?? chartDef.series[0]?.metricId;
  return metricId === defaultMetricId && direction === 'desc'
    ? undefined
    : { metricId, direction };
};

const guessLimit = (
  chartDef: V5ChartDefinition,
): Studio.TableLimit | undefined => {
  const mode = chartDef.groupByLimitMode;
  if (mode && typeof mode === 'object' && mode.type === 'exclude top n') {
    return compact<Studio.TableLimit>({
      mode: 'excludeTop',
      n: mode.n === GROUP_LIMIT ? undefined : mode.n,
      excludeBy: guessExcludeBy({ chartDef, excludeSort: mode.excludeSort }),
    }) as Studio.TableLimit;
  }
  // A larger limit without a mode lists every term (see isUnlimitedMatrix)
  if (
    mode === 'top n' ||
    (!mode &&
      chartDef.groupByLimit !== undefined &&
      !isUnlimitedMatrix(chartDef))
  ) {
    return compact<Studio.TableLimit>({
      mode: 'top',
      n:
        chartDef.groupByLimit === undefined ||
        chartDef.groupByLimit === GROUP_LIMIT
          ? undefined
          : chartDef.groupByLimit,
    }) as Studio.TableLimit;
  }
  // Every term is the default
  return undefined;
};

// Each metric's options, as its series has them. Following the report's
// comparison, a metric without a cell type shows the % change, so one the
// classic editor left without shows none.
const guessMetricOptions = ({
  chartDef,
  followsReport,
  keys,
}: {
  chartDef: V5ChartDefinition;
  followsReport: boolean;
  keys?: string[];
}): Record<string, Studio.TableMetricOptions> | undefined => {
  const entries = chartDef.series.flatMap((s, index) => {
    const cellType = ((): Studio.TableMetricOptions['cellType'] => {
      if (followsReport) {
        if (!s.matrixCellType) {
          return 'none';
        }
        return s.matrixCellType === DEFAULT_REPORT_CELL_TYPE
          ? undefined
          : s.matrixCellType;
      }
      return s.matrixCellType;
    })();
    const options = compact<Studio.TableMetricOptions>({
      displayName: s.displayName || undefined,
      isTotalHidden: s.isTotalEnabled === false || undefined,
      isStatusFlag: s.isStatusFlag || undefined,
      cellType,
      showComparisonValue: s.matrixShowComparisonValue || undefined,
      isRankEnabled: s.isRankingEnabled || undefined,
      isPercentageOfTotalEnabled: s.isPercentageOfTotalEnabled || undefined,
      percentageOfTotalPrecision: s.isPercentageOfTotalEnabled
        ? s.percentageOfTotalPrecision
        : undefined,
      cellColourConfig: s.cellColourConfig,
    });
    return options
      ? [[(keys && keys[index]) ?? s.metricId, options] as const]
      : [];
  });
  return entries.length > 0 ? Object.fromEntries(entries) : undefined;
};

// A Table block's options from a matrix the classic editor saved (or Studio
// saved without its settings): the reverse of buildTableDefinition, leaving
// out what it builds by default. Whether they rebuild the matrix is checked
// separately (reproducesChart), so a guess that's wrong leaves it Classic.
export const guessTableOptions = ({
  chartDef: saved,
  dimensions,
  comparison,
  isReportGadget,
  keys,
}: {
  chartDef: V5ChartDefinition;
  // As Studio reads them (guessDimensions)
  dimensions: string[];
  comparison?: PersistedComparisonType;
  // A report gadget without a comparison of its own can follow the
  // report's
  isReportGadget: boolean;
  // The block's entry for each series (see seriesVariants), which its
  // options are keyed by; its metric by default
  keys?: string[];
}): Studio.TableOptions | undefined => {
  // Its sort as it's drawn, however the classic editor saved it
  const chartDef = withRenderedMatrixSort(saved);
  const hasDimension = dimensions.length > 0;
  const columns = guessColumns(chartDef);
  const usesInterval = !hasDimension || columns === 'dates';
  const defaultInterval =
    columns === 'dates' ? DEFAULT_COLUMN_INTERVAL : DEFAULT_ROW_INTERVAL;
  const interval =
    usesInterval &&
    chartDef.trendByCalendarInterval &&
    chartDef.trendByCalendarInterval !== defaultInterval
      ? chartDef.trendByCalendarInterval
      : undefined;
  const tableComparison: Studio.TableComparison | undefined =
    comparison && isStudioCompareType(comparison.compareType)
      ? { compareType: comparison.compareType }
      : // Following the report's shows only where a metric has cells
        isReportGadget &&
          !comparison &&
          chartDef.series.some((s) => !!s.matrixCellType)
        ? { compareType: 'report' }
        : undefined;
  const followsReport =
    !!tableComparison && tableComparison.compareType === 'report';

  return compact<Studio.TableOptions>({
    columns: columns === 'metrics' ? undefined : columns,
    interval,
    excludePartialIntervals:
      (usesInterval && chartDef.excludePartialIntervals) || undefined,
    sort: guessSort({ chartDef, hasDimension }),
    // Rows that aren't grouped (e.g. dates) send no limit
    limit: hasDimension ? guessLimit(chartDef) : undefined,
    comparison: tableComparison,
    gridSizing: chartDef.gridSizing === 'fitContent' ? 'fitContent' : undefined,
    reverseAxes: chartDef.reverseAxes || undefined,
    totalsColumnPosition:
      chartDef.reverseAxes && chartDef.totalsColumnPosition === 'first'
        ? 'first'
        : undefined,
    isSecondaryRowsStacked:
      (chartDef.isSecondaryRowsStacked &&
        isSecondaryDimensionAInEffect(chartDef)) ||
      undefined,
    metrics: guessMetricOptions({ chartDef, followsReport, keys }),
  });
};
